import {
  isDiseaseId,
  isGeneId,
  isPhenotypeId
} from 'src/components/admin/appReducer';

// What a legend drop writes, and to whom. Pure, so every rule below is pinned by
// tests rather than living inside a drag handler.
//
// ONE ADDRESSING RULE WITH THE SIDEBAR. The Phenotypes, Genotypes and Diseases
// sections post { user_id: selectedUser.user_id || selectedUser._id,
// familyMemberId: proBandId ?? familyMemberId, isProband: !!proBandId }. The
// legends had their own spelling of the owner (`_id ?? user_id`). It resolves to
// the same root today — the root row carries no user_id — but two spellings of
// one rule is how they drift, so the drop now uses the sidebar's.

export const RECORD_KIND = {
  disease: 'disease',
  phenotype: 'phenotype',
  genotype: 'genotype'
};

/**
 * The kind of record a DISEASES-legend entry makes. That legend can still list a
 * gene or an HPO term on legacy data, so it has to go by the id's prefix.
 *
 * ONLY for that legend. The Phenotypes and Genotypes legends say what they are:
 * going by the prefix sent a genotype whose id is not `NCBIGene:…` to
 * POST /diagnoses, which stored it on the person as an affected disease.
 */
export const recordKindOf = (item) => {
  if (isGeneId(item?.id)) return RECORD_KIND.genotype;
  if (isPhenotypeId(item?.id)) return RECORD_KIND.phenotype;
  return RECORD_KIND.disease;
};

export const ownerIdOf = (selectedUser) =>
  selectedUser?.user_id || selectedUser?._id || null;

const nameKey = (n) => (n || '').trim().toLowerCase();

/**
 * Does this person already have it? Mirrors the sidebar's add guards: phenotypes
 * and genotypes by id (legacy rows keep them in `diagnoses` too), diseases by id
 * OR by name, because the same disease under a second coding system puts two
 * identical-looking records on one person.
 */
export const alreadyRecorded = (kind, member, item) => {
  const diagnoses = member?.diagnoses || [];
  if (kind === RECORD_KIND.phenotype) {
    return [...(member?.phenotypes || []), ...diagnoses].some(
      (p) => p?.id === item.id
    );
  }
  if (kind === RECORD_KIND.genotype) {
    return [...(member?.genotypes || []), ...diagnoses].some(
      (g) => g?.id === item.id
    );
  }
  // By name only against DISEASES: legacy rows still keep HPO terms and genes in
  // `diagnoses`, and the sidebar's guard never compares against those.
  return diagnoses.some(
    (d) =>
      d?.id === item.id ||
      (isDiseaseId(d?.id) && nameKey(d?.name) === nameKey(item.name))
  );
};

/**
 * The POST body for dropping `item` on `member`.
 *
 * THE DISEASE ENTRY IS WHITELISTED. A diseases-legend entry is the FIRST family
 * member's stored diagnosis (getUniqueDiagnoses keeps the first it sees), and the
 * drop used to post `{ ...entry, age }`. The server resets only colour, the three
 * flags and `_id`, so the target person silently inherited the source person's
 * comment, recordedBy, dateReported, affectedStatus (e.g. 'carrier'),
 * carrierBasis with its variant reference, inheritanceMode, karyotype and
 * mosaicism — another patient's clinical content under this patient's name. And
 * the comment typed into the drop dialog was never sent at all.
 *
 * A genotype is `{ id, name }` plus the provenance the sidebar's add stamps (a
 * legend genotype carries its source person's variants, which must not travel);
 * a phenotype is stamped `patientReported: false` exactly as the sidebar's
 * quick-add does (TGCLOUD-1823).
 *
 * @param {string} [recordedBy] the signed-in clinician, for a genotype
 * @param {string} [recordedOn] today as 'YYYY-MM-DD' (toStoredDate), for a genotype
 */
export const buildLegendRecordPayload = ({
  kind,
  selectedUser,
  member,
  item,
  age,
  comment,
  recordedBy,
  recordedOn
}) => {
  const base = {
    familyId: selectedUser?.familyId,
    familyMemberId: member?.proBandId ?? member?.familyMemberId,
    user_id: ownerIdOf(selectedUser),
    isProband: Boolean(member?.proBandId)
  };

  if (kind === RECORD_KIND.phenotype) {
    return {
      ...base,
      phenotypes: [{ id: item.id, name: item.name, patientReported: false }]
    };
  }
  if (kind === RECORD_KIND.genotype) {
    // The sidebar's add stamps these on every new genotype, and POST /genotypes
    // defaults none of them: without them a dropped genotype had no recorder, no
    // date, and a patientReported that read as unknown.
    return {
      ...base,
      genotypes: [
        {
          id: item.id,
          name: item.name,
          clinicallyConfirmed: false,
          patientReported: false,
          displayOnPedigree: true,
          dateRecorded: recordedOn,
          recordedBy,
          carrierStatus: '',
          selectedClinVarIds: []
        }
      ]
    };
  }

  const diagnosis = { id: item.id, name: item.name };
  if (age !== undefined && age !== null && String(age).trim() !== '') {
    diagnosis.age = String(age).trim();
  }
  if (comment && String(comment).trim()) {
    diagnosis.comment = String(comment).trim();
  }
  return { ...base, diagnoses: [diagnosis] };
};
